// check_login.js

import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import React from 'react';
import { Loading } from '@components';
import { loginAction } from '@flux/action/loginAction';
import { getPostLoginRedirectPath } from '@utils/postLoginRedirect';

const FullLoading = ({ isLoading, tip = 'loading...' }) =>
  isLoading && (
    <div className="loading-wrapper-fullscreen">
      <Loading tip={tip} />
    </div>
  );

// ============================================
// ROUTE CONFIGURATION
// ============================================

// Routes accessible WITHOUT authentication
const PUBLIC_ROUTES = ['/login', '/registration', '/register', '/error'];

// Routes that logged-in users should be REDIRECTED AWAY from
// (subset of PUBLIC_ROUTES - login pages only)
const REDIRECT_IF_LOGGED_IN = ['/login'];

// ============================================
// HELPER FUNCTIONS
// ============================================

// Check if pathname matches any route in the list
const matchesRoute = (pathname, routes) => {
  const normalizedPath = pathname.toLowerCase();
  return routes.some(
    (route) =>
      normalizedPath === route.toLowerCase() ||
      normalizedPath.startsWith(route.toLowerCase() + '/'),
  );
};

// ============================================
// CHECK LOGIN HOC
// ============================================

const checkLogin = (CurrentComponent) => {
  function FinalComponent(props) {
    const { isLogin, history, getLogin, getUserInfoFromToken, location } =
      props;
    const { ...params } = props;

    const pathname = location?.pathname || history.location.pathname;
    const loginProgress = window.sessionStorage.getItem('loginProgress');
    const isGettingCode = window.location.search.startsWith('?code');

    // Determine route type
    const isPublicRoute = matchesRoute(pathname, PUBLIC_ROUTES);
    const shouldRedirectIfLoggedIn = matchesRoute(
      pathname,
      REDIRECT_IF_LOGGED_IN,
    );

    // ============================================
    // CASE 1: Handle OAuth callback (?code=...)
    // ============================================
    if (isGettingCode) {
      if (!isLogin && loginProgress) {
        getLogin(`${window.location.search}`)
          .then((token) => {
            if (token) {
              getUserInfoFromToken().then(() => {
                const redirectTo = getPostLoginRedirectPath();
                history.replace(redirectTo);
              });
            }
          })
          .catch((error) => {
            console.error('=== getLogin Error:', error);
            window.sessionStorage.removeItem('loginProgress');
            history.replace('/login');
          });
      }

      return <FullLoading isLoading={true} tip="Authenticating..." />;
    }

    // ============================================
    // CASE 2: User IS logged in
    // ============================================
    if (isLogin) {
      // Logged-in user on login page → Redirect to app
      if (shouldRedirectIfLoggedIn) {
        const redirectTo = getPostLoginRedirectPath();
        return <Redirect to={redirectTo} />;
      }

      // Logged-in user on any other page → Allow access
      return <CurrentComponent {...params} />;
    }

    // ============================================
    // CASE 3: User is NOT logged in
    // ============================================
    // Clear login progress if not processing OAuth
    window.sessionStorage.removeItem('loginProgress');

    // Not logged in + Public route → Allow access
    if (isPublicRoute) {
      return <CurrentComponent {...params} />;
    }

    // Not logged in + Protected route → Save URL & Redirect to login

    // Save current URL for redirect after login
    if (pathname !== '/') {
      const fullUrl = window.location.href;
      window.localStorage.setItem('from', encodeURIComponent(fullUrl));
    }

    return <Redirect to="/login" />;
  }

  const WithRouterComponent = withRouter(FinalComponent);

  return connect(
    (state) => ({
      isLogin: state.login.isLogin,
      beforeLogout: state.login.beforeLogout,
    }),
    {
      getLogin: loginAction.getLogin,
      getUserInfoFromToken: loginAction.getUserInfoFromToken,
    },
  )(WithRouterComponent);
};

export default checkLogin;
